import { useMemo } from 'react';

import { useMy } from '@/hooks/useMy';
import { Setting } from '@/types';

const SUGGESTION_PREFIX = 'integration-suggestion:';

/**
 * Fetches integration-suggestion Setting records from DynamoDB via /my and
 * returns a list of integration names the user hasn't configured.
 *
 * The correlation engine creates one Setting per unconfigured provider
 * (e.g. key "integration-suggestion:amazon", value true) when CanCorrelate
 * fires during batch correlation.  The user can dismiss via PUT /setting
 * which sets value to false.
 */
export function useIntegrationSuggestions() {
  const { data: settings, ...rest } = useMy(
    {
      resource: 'setting',
      query: SUGGESTION_PREFIX,
    },
    {
      // Only refetch periodically — suggestions don't change rapidly.
      refetchInterval: 5 * 60 * 1000,
    }
  );

  const suggestions = useMemo(() => {
    if (!settings || settings.length === 0) return [];

    return (settings as Setting[])
      .filter(
        s =>
          String(s.value) !== 'false' && s.name?.startsWith(SUGGESTION_PREFIX)
      )
      .map(s => s.name.slice(SUGGESTION_PREFIX.length));
  }, [settings]);

  return {
    ...rest,
    suggestions,
  };
}
